import React, { useCallback, useMemo, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { Pencil, Plus } from 'lucide-react';
import { LopCrudPage } from '../../components/LopCrudPage';
import { SpecialCreationLimitFormModal } from './SpecialCreationLimitFormModal';
import { useFunds } from '../../hooks/useFunds';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit as canEditUtil } from '../../utils/permissions';
import {
  getSpecialCreationLimits,
  saveSpecialCreationLimitsBatch,
} from '../../api/specialCreationLimits';
import type { SpecialCreationLimit } from '../../types/SpecialCreationLimit';
import { t } from '../../i18n/t';
import './SpecialCreationLimitsPage.css';

const RESOURCE_CODE = 'specialCreationLimits';

interface EditCellRendererExtraProps {
  onEdit: (row: SpecialCreationLimit) => void;
  disabled: boolean;
}

type EditCellRendererProps = CustomCellRendererProps<SpecialCreationLimit> &
  EditCellRendererExtraProps;

// A dedicated per-row action button — NOT inline cell editing. Both add AND
// edit go through SpecialCreationLimitFormModal, which is the only place
// that can guarantee "exactly one of limitQty/limitValue" (a radio picks
// which one, the other is always sent as null).
function EditCellRenderer({ data, onEdit, disabled }: EditCellRendererProps) {
  if (!data) return null;
  return (
    <button
      type="button"
      className="special-creation-limits__edit-btn"
      onClick={() =>
        onEdit({
          fundId: data.fundId,
          fundName: data.fundName,
          fundAssetId: data.fundAssetId,
          limitQty: data.limitQty,
          limitValue: data.limitValue,
        })
      }
      disabled={disabled}
      title={t('specialCreationLimits.editRow')}
      aria-label={t('specialCreationLimits.editRow')}
    >
      <Pencil size={14} />
    </button>
  );
}

export function SpecialCreationLimitsPage() {
  const { user } = useAuth();
  const canEdit = canEditUtil(user, RESOURCE_CODE);

  // Tenant-scoped lookup; also the fix for the hard-refresh X-Tenant-Id race
  // — useFunds calls setTenantId itself (see its module comment) before
  // firing its fetch. Gating the page (and thus LopCrudPage's first getRows
  // call) on fundsLoading guarantees the tenant header is set in time.
  // Mirrors CapitalConsolidationsPage / BankAccountsPage.
  const { data: funds, loading: fundsLoading, error: fundsError } = useFunds();

  // Bumped after the form creates/edits a row, so getRows gets a new identity
  // and useCrudGrid re-fetches (mirrors the ForwardInterestSettings recipe —
  // add/edit never go through the grid's own dirty tracking here).
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  // null = add mode, non-null = edit mode (form opens pre-filled with this row).
  const [editingRow, setEditingRow] = useState<SpecialCreationLimit | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const wrappedGetRows = useCallback(
    () => getSpecialCreationLimits(),
    // refreshVersion intentionally part of the identity so a form submit re-fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshVersion],
  );

  const saveBatch = useCallback(
    (
      added: SpecialCreationLimit[],
      modified: SpecialCreationLimit[],
      deletedIds: unknown[],
    ) =>
      // Grid-native added/modified are always empty here — showAdd={false} and
      // no grid column is editable, so the only thing useCrudGrid's own Save
      // button ever submits is deletes. Adds/edits go through handleFormSubmit.
      saveSpecialCreationLimitsBatch(added, modified, deletedIds as number[]),
    [],
  );

  const handleOpenAdd = useCallback(() => {
    setEditingRow(null);
    setFormError(null);
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((row: SpecialCreationLimit) => {
    setEditingRow(row);
    setFormError(null);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingRow(null);
    setFormError(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (payload: SpecialCreationLimit) => {
      setSubmitting(true);
      setFormError(null);
      try {
        if (editingRow) {
          await saveSpecialCreationLimitsBatch([], [payload], []);
        } else {
          await saveSpecialCreationLimitsBatch([payload], [], []);
        }
        setRefreshVersion((v) => v + 1);
        setModalOpen(false);
        setEditingRow(null);
      } catch (err: unknown) {
        setFormError(err instanceof Error ? err.message : String(err));
      } finally {
        setSubmitting(false);
      }
    },
    [editingRow],
  );

  const columnDefs = useMemo<ColDef<SpecialCreationLimit>[]>(
    () => [
      {
        colId: 'edit',
        headerName: '',
        width: 52,
        sortable: false,
        filter: false,
        resizable: false,
        editable: false,
        cellRenderer: EditCellRenderer,
        cellRendererParams: {
          onEdit: handleOpenEdit,
          disabled: !canEdit,
        } as EditCellRendererExtraProps,
      },
      {
        field: 'fundAssetId',
        headerName: t('specialCreationLimits.columns.fundAssetId'),
        width: 140,
        editable: false,
        cellDataType: 'number',
        sort: 'asc',
      },
      {
        field: 'fundName',
        headerName: t('specialCreationLimits.columns.fundName'),
        flex: 1,
        minWidth: 220,
        editable: false,
      },
      {
        // Display-only by design (see module comment) — editing goes through
        // SpecialCreationLimitFormModal, never inline.
        field: 'limitQty',
        headerName: t('specialCreationLimits.columns.limitQty'),
        width: 160,
        editable: false,
        cellDataType: 'number',
      },
      {
        field: 'limitValue',
        headerName: t('specialCreationLimits.columns.limitValue'),
        width: 160,
        editable: false,
        cellDataType: 'number',
      },
    ],
    [handleOpenEdit, canEdit],
  );

  if (fundsError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('specialCreationLimits.errorPrefix', { message: fundsError })}
        </div>
      </div>
    );
  }

  if (fundsLoading || !funds) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <div className="special-creation-limits">
      <div className="special-creation-limits__toolbar">
        <button
          type="button"
          className="special-creation-limits__add"
          onClick={handleOpenAdd}
          disabled={!canEdit}
          title={t('specialCreationLimits.form.addButton')}
          aria-label={t('specialCreationLimits.form.addButton')}
        >
          <Plus size={16} />
          <span>{t('specialCreationLimits.form.addButton')}</span>
        </button>
      </div>
      <div className="special-creation-limits__crud">
        <LopCrudPage<SpecialCreationLimit>
          resourceCode={RESOURCE_CODE}
          titleKey="specialCreationLimits.pageTitle"
          idField="fundId"
          getRows={wrappedGetRows}
          saveBatch={saveBatch}
          columnDefs={columnDefs}
          showAdd={false}
        />
      </div>
      {modalOpen && (
        <SpecialCreationLimitFormModal
          funds={funds}
          initialRow={editingRow}
          submitting={submitting}
          submitError={formError}
          onSubmit={(payload) => {
            void handleFormSubmit(payload);
          }}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
